import { Content, ContentRelationshipField } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  CaseStudyDocument,
  SimpleCaseStudySliceDefault,
  SimpleCaseStudySliceSummary,
  TestimonialDocument,
} from '../../../prismicio-types';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { Url } from 'url';
import ChallengeAndSolution from './variants/ChallengeAndSolution';

enum SLICE_VARIATION {
  default = 'default',
  withTestimonial = 'summary',
  challengeAndSolution = 'challengeAndSolution',
}

/**
 * Props for `SimpleCaseStudy`.
 */
export type SimpleCaseStudyProps =
  SliceComponentProps<Content.SimpleCaseStudySlice>;

/**
 * Component for "SimpleCaseStudy" Slices.
 */

const SimpleCaseStudy = ({ slice }: SimpleCaseStudyProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.withTestimonial &&
        withTestimonialVariation(slice)}
      {slice.variation === SLICE_VARIATION.challengeAndSolution && (
        <ChallengeAndSolution slice={slice} />
      )}
    </section>
  );
};

const defaultVariation = (slice: SimpleCaseStudySliceDefault): JSX.Element => {
  const typedRelationshipFieldCaseStudy = slice.primary
    .case_study as ContentRelationshipField & CaseStudyDocument;

  return (
    <div className="text-white flex flex-col px-5 md:px-16 py-20 md:py-24 max-w-screen-2xl mx-auto">
      <div className="text-center flex flex-col md:gap-6 gap-3 mb-20 lg:px-36 md:px-16 sm:px-8">
        <PrismicRichText field={slice.primary.title} />
      </div>
      <div className="card lg:flex-row md:flex-row sm:flex-col lg:h-96 md:h-96 sm:h-auto">
        <div className="lg:h-full md:h-full sm:h-96 pl-6 pt-6 pr-6 flex items-center justify-center lg:w-1/2 md:w-1/2 sm:w-full">
          <div className="justify-left">
            <div className="mb-2">
              <PrismicRichText field={slice.primary.case_study_title} />
            </div>
            <div className="mb-6">
              <PrismicRichText field={slice.primary.case_study_description} />
            </div>
            <PrismicNextLink
              href={typedRelationshipFieldCaseStudy.url as unknown as Url}
              className="button-outline-primary"
            >
              <span>{slice.primary.button_label}</span>
            </PrismicNextLink>
          </div>
        </div>
        <div className="lg:w-1/2 md:w-1/2 sm:w-full overflow-hidden">
          <PrismicNextImage
            field={typedRelationshipFieldCaseStudy.data.image}
            className="rounded-b-[20px] lg:rounded-r-[20px] lg:rounded-l-[0px] h-36 md:h-full lg:h-full object-cover object-center"
          />
        </div>
      </div>
    </div>
  );
};

const withTestimonialVariation = (
  slice: SimpleCaseStudySliceSummary,
): JSX.Element => {
  const {
    icon,
    small_title: smallTitle,
    title,
    testimonial,
    industry,
    technology,
    platform,
  } = slice.primary;
  const typedTestimonial =
    testimonial as ContentRelationshipField<'testimonial'> &
      TestimonialDocument;
  return (
    <div className="bg-mint-green-gradient-4">
      <div className="max-w-screen-2xl mx-auto px-5 md:px-16 py-20">
        <div className="rounded-[20px] border border-primary-shade-green-black flex flex-col xl:flex-row">
          <div className="flex flex-col gap-y-6 p-6 xl:p-16">
            <PrismicNextImage field={icon} />
            <span className="-mt-3">
              <PrismicRichText field={smallTitle} />
            </span>
            <PrismicRichText field={title} />
            <PrismicRichText
              field={typedTestimonial.data.quote}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-regular-normal">{children}</p>
                ),
              }}
            />
            <div className="flex gap-x-5 items-center">
              <div className="hidden md:block">
                <PrismicNextImage field={typedTestimonial.data.avatar} />
              </div>
              <div>
                <PrismicRichText field={typedTestimonial.data.name} />
                <PrismicRichText field={typedTestimonial.data.job_title} />
              </div>
              <figure className="h-full w-[1px] bg-black" />
              <PrismicNextImage field={typedTestimonial.data.company_logo} />
            </div>
          </div>
          <div className="flex flex-col xl:border-l xl:border-primary-gray justify-around xl:w-[32.81rem] xl:flex-shrink-0">
            <div className="p-6">
              <h4 className="text-h5">Industry</h4>
              <PrismicRichText
                field={industry}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-large-normal">{children}</p>
                  ),
                }}
              />
            </div>
            <div className="p-6 border-y-[1px] border-primary-gray">
              <h4 className="text-h5">Platform</h4>
              <PrismicRichText
                field={platform}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-large-normal">{children}</p>
                  ),
                }}
              />
            </div>
            <div className="p-6">
              <h4 className="text-h5 mb-4">Technology</h4>
              <PrismicNextImage field={technology} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleCaseStudy;
